import Card from "../Card";
import FormField from "../FormField";
import SectionHeader from "../SectionHeader";
import SourceBanner from "../SourceBanner";
import { ProgressReportPrefill } from "../../../../../hooks/useConstructionProgressReport";
import Table, { Column } from "Components/Table/Table";

interface Props {
  prefill?: ProgressReportPrefill;
  lienWaiverMinAmount?: string;
  onLienWaiverMinAmountChange?: (val: string) => void;
  lienWaiverDescription?: string;
  onLienWaiverDescriptionChange?: (val: string) => void;
  lienWaiverNote?: string;
  onLienWaiverNoteChange?: (val: string) => void;
}

const fmt = (amount?: number): string =>
  amount !== undefined
    ? `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
    : "—";

const fmtDate = (iso?: string): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const Step5LienWaivers = ({
  prefill,
  lienWaiverMinAmount = "",
  onLienWaiverMinAmountChange,
  lienWaiverDescription = "",
  onLienWaiverDescriptionChange,
  lienWaiverNote = "",
  onLienWaiverNoteChange,
}: Props) => {
  const waiversByDraw = prefill?.lienWaiversByDraw ?? [];

  type WaiverItem = (typeof waiversByDraw)[number]["waivers"][number];
  type FlatRow =
    | { _type: "group"; drawId: string; drawNumber: string | number }
    | { _type: "item"; waiver: WaiverItem };

  const flatRows: FlatRow[] = waiversByDraw.flatMap((drawGroup) => [
    {
      _type: "group" as const,
      drawId: drawGroup.drawId,
      drawNumber: drawGroup.drawNumber,
    },
    ...drawGroup.waivers.map((waiver) => ({ _type: "item" as const, waiver })),
  ]);

  const lienColumns: Column<FlatRow>[] = [
    {
      header: "Vendor",
      render: (row) => (row._type === "item" ? row.waiver.companyName : null),
    },
    {
      header: "Amount Earned",
      render: (row) =>
        row._type === "item" ? fmt(row.waiver.totalAmount?.amount ?? 0) : null,
    },
    {
      header: "Less Retainage",
      render: (row) =>
        row._type === "item"
          ? fmt(row.waiver.retainageAmount?.amount ?? 0)
          : null,
    },
    {
      header: "Received",
      render: (row) =>
        row._type === "item" ? fmt(row.waiver.paidAmount?.amount ?? 0) : null,
    },
    {
      header: "Date Received",
      render: (row) =>
        row._type === "item" ? fmtDate(row.waiver.signedAt) : null,
    },
    {
      header: "Delta",
      render: (row) =>
        row._type === "item"
          ? fmt(
              (row.waiver.remainingAmount?.amount ?? 0) -
                (row.waiver.paidAmount?.amount ?? 0),
            )
          : null,
    },
  ];

  return (
    <div className="craScreen">
      <SectionHeader
        title="Required Waivers of Lien"
        subtitle="Lien waiver tracking table auto-populated from platform data. Edit amounts or add vendors as needed. This table appears in the published report."
      />
      <SourceBanner
        icon="📊"
        title="Auto-populated from Platform Lien Waiver Tracking"
        text="Review waivers below and confirm receipt status before proceeding."
      />

      <Card
        title="Lien Waiver Disclaimer"
        badge={{ label: "Editable", variant: "editable" }}
      >
        <div className="craFieldRow cols2">
          <FormField
            label="Disclaimer Line 1"
            value={lienWaiverDescription}
            onChange={onLienWaiverDescriptionChange}
          />
          <FormField
            label="Minimum Waiver Threshold"
            value={lienWaiverMinAmount}
            onChange={onLienWaiverMinAmountChange}
            type="number"
            otherProps={{ min: 0 }}
            icon="$"
          />
        </div>
        <FormField
          label="Additional Note"
          value={lienWaiverNote}
          onChange={onLienWaiverNoteChange}
        />
      </Card>

      <Card
        title="Lien Waiver Table"
        badge={{ label: "Auto-populated", variant: "auto" }}
      >
        <div className="tableWrap">
          <Table
            tableClassName="lienTable"
            data={flatRows}
            rowClassName={(row) => (row._type === "group" ? "group" : "")}
            columns={lienColumns}
            spanRow={(row) =>
              row._type === "group" ? `Draw #${row.drawNumber}` : undefined
            }
            fallbackUI={
              <span style={{ color: "#999" }}>No lien waivers found</span>
            }
          />
        </div>
        <div className="craNote">
          Waiver deltas are due with the next submission. Negative deltas
          representing an overage in waivers submitted will be highlighted green
          and carried down as needed.
        </div>
      </Card>
    </div>
  );
};

export default Step5LienWaivers;
